/**
 * Development sign-in: the API accepts `dev:<email>` (and `dev:<email>:mfa`)
 * tokens only when it runs with AUTH_MODE=dev, and refuses to start that way
 * in production.
 */
import { AuthFlowError, type AuthProvider, type AuthSession, type TotpEnrolment } from './types';

const KEY = 'sss.auth.dev';

export class DevAuthProvider implements AuthProvider {
  readonly mode = 'dev' as const;
  private readonly listeners = new Set<() => void>();

  private read(): { email: string; mfa: boolean } | null {
    try {
      const v = localStorage.getItem(KEY);
      return v ? (JSON.parse(v) as { email: string; mfa: boolean }) : null;
    } catch {
      return null;
    }
  }

  private write(v: { email: string; mfa: boolean } | null) {
    if (v) localStorage.setItem(KEY, JSON.stringify(v));
    else localStorage.removeItem(KEY);
    this.listeners.forEach((l) => l());
  }

  current(): AuthSession | null {
    const s = this.read();
    return s ? { email: s.email, method: 'dev' } : null;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async getToken(): Promise<string | null> {
    const s = this.read();
    return s ? `dev:${s.email}${s.mfa ? ':mfa' : ''}` : null;
  }

  async signInDev(email: string, withMfa: boolean): Promise<void> {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+$/.test(e)) throw new AuthFlowError('INVALID_CREDENTIALS');
    this.write({ email: e, mfa: withMfa });
  }

  async signInWithGoogle(_opts?: { loginHint?: string }): Promise<void> {
    throw new AuthFlowError('NOT_CONFIGURED', 'Google sign-in is not available in dev mode');
  }

  async signInWithG4SId(_opts?: { loginHint?: string }): Promise<void> {
    throw new AuthFlowError('NOT_CONFIGURED', 'G4SID sign-in is not available in dev mode');
  }

  async completeRedirect(): Promise<boolean> {
    return false;
  }

  async signInWithPassword(email: string): Promise<{ status: 'signed-in' }> {
    await this.signInDev(email, false);
    return { status: 'signed-in' };
  }

  async verifyMfa(): Promise<void> {
    const s = this.read();
    if (s) this.write({ ...s, mfa: true });
  }

  async startTotpEnrolment(): Promise<TotpEnrolment> {
    const secret = 'JBSWY3DPEHPK3PXP';
    const email = this.read()?.email ?? 'user';
    return { secret, codeLength: 6, otpauthUrl: `otpauth://totp/SSS:${email}?secret=${secret}&issuer=SSS` };
  }

  async finishTotpEnrolment(): Promise<void> {
    const s = this.read();
    if (s) this.write({ ...s, mfa: true });
  }

  async sendPasswordReset(): Promise<void> {}

  async signOut(): Promise<void> {
    this.write(null);
  }
}
